/**
 * "Copy link": a click-open disclosure listing the shareable URL(s) of the
 * current view, each with its own Copy button.
 *
 * Copy uses the async clipboard API; where it is missing or refused (an
 * insecure origin, a denied permission) the URL's input is selected instead so
 * ⌘C / Ctrl+C finishes the job, and the button says so.
 *
 * Escape closes THIS popover and stops there — it must not also close the
 * drawer around it — and hands focus back to the trigger.
 */
import { Link2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

export interface ShareLink {
  id: string;
  label: string;
  description: string;
  url: string;
}

function LinkRow({ link }: { link: ShareLink }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [status, setStatus] = useState<"idle" | "copied" | "select">("idle");

  async function copy() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(link.url);
      setStatus("copied");
    } catch {
      inputRef.current?.focus();
      inputRef.current?.select();
      setStatus("select");
    }
  }

  return (
    <div data-testid={`copy-link-row-${link.id}`}>
      <div className="text-label text-text">{link.label}</div>
      <p className="text-caption text-text-secondary">{link.description}</p>
      <div className="mt-1 flex gap-2">
        <input
          ref={inputRef}
          readOnly
          value={link.url}
          aria-label={`${link.label} link`}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 rounded-sm border border-border bg-bg px-2 py-1 text-caption text-text"
        />
        <button
          type="button"
          onClick={() => void copy()}
          data-testid={`copy-link-button-${link.id}`}
          className="rounded-md border border-border px-3 py-1 text-label text-text hover:border-accent hover:text-accent-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {status === "copied" ? "Copied" : status === "select" ? "Press Ctrl+C / ⌘C" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export function CopyLinkPopover({ links }: { links: ShareLink[] }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid="copy-link"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-label text-text hover:border-accent hover:text-accent-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <Link2 className="h-4 w-4" aria-hidden="true" />
        Copy link
      </button>
      {open && (
        <section
          id={panelId}
          aria-label="Shareable links"
          data-testid="copy-link-panel"
          className="absolute right-0 top-full z-10 mt-2 flex w-96 flex-col gap-3 rounded-md border border-border bg-surface p-3 shadow-lg"
        >
          {links.map((l) => (
            <LinkRow key={l.id} link={l} />
          ))}
        </section>
      )}
    </div>
  );
}
